import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, Users, BookOpen, Zap,
  TrendingUp, ClipboardList, Eye, Lock, CheckCircle, Target,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useSpace } from '../context/SpaceContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Referential Competency Panel ──────────────────────────────────────────────
function ReferentialPanel({ spaceId }) {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersCount, setMembersCount] = useState(0);

  useEffect(() => {
    if (!spaceId) { setLoading(false); return; }
    axios.get(`${API}/spaces/${spaceId}/competencies`, { withCredentials: true })
      .then(r => {
        setComps(r.data.competencies || []);
        setMembersCount(r.data.members_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [spaceId]);

  if (!spaceId) {
    return (
      <div className="kurso-card p-6 text-center">
        <Target size={24} className="text-[#a0a0ab] mx-auto mb-2" />
        <p className="text-[#a0a0ab] text-sm">Sélectionnez un espace organisation pour voir les compétences.</p>
      </div>
    );
  }
  if (loading) return <div className="animate-shimmer h-28 rounded-2xl" />;
  if (comps.length === 0) {
    return (
      <div className="kurso-card p-6 text-center">
        <Target size={24} className="text-[#a0a0ab] mx-auto mb-2" />
        <p className="text-[#a0a0ab] text-sm">Aucun cours aligné sur un référentiel dans cet espace.</p>
        <p className="text-xs text-[#a0a0ab] mt-1">
          Les membres doivent aligner leurs cours sur des référentiels.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#a0a0ab]">{membersCount} membres · {comps.length} référentiels couverts</p>
      {comps.map(comp => {
        const pct = comp.total_courses > 0 ? Math.round((comp.completed_courses / comp.total_courses) * 100) : 0;
        return (
          <div key={comp.name} data-testid={`org-comp-${comp.name}`} className="kurso-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#ff6b6b]/10 flex items-center justify-center shrink-0">
                  <Target size={12} className="text-[#ff6b6b]" />
                </div>
                <span className="text-white text-sm font-semibold">{comp.name}</span>
              </div>
              <span className="text-xs text-[#a0a0ab]">
                {comp.members_reached} membre{comp.members_reached !== 1 ? 's' : ''} actifs
              </span>
            </div>
            <div className="progress-bar-bg h-1.5 mb-1.5">
              <div className="progress-bar-fill h-1.5 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#a0a0ab]">
                {comp.completed_courses}/{comp.total_courses} cours terminés
              </span>
              <span
                className="text-xs font-bold"
                style={{ color: pct >= 75 ? '#20c997' : pct >= 40 ? '#ffc107' : '#ff6b6b' }}
              >
                {pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function OrgDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { spaces } = useSpace();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [bilanData, setBilanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');

  // Find the organisation space owned by this user
  const orgSpace = spaces?.find(s =>
    s.type === 'organisation' && (s.owner_id === user?.id || s.role === 'admin_principal')
  );

  useEffect(() => {
    if (user?.plan !== 'organisation' && user?.role !== 'admin') {
      navigate('/pricing');
      return;
    }
    loadOrg();
  }, [user, navigate]);

  const loadOrg = async () => {
    try {
      const [orgRes, bilanRes] = await Promise.all([
        axios.get(`${API}/org`, { withCredentials: true }),
        axios.get(`${API}/bilan/admin/org`, { withCredentials: true }).catch(() => null),
      ]);
      setOrg(orgRes.data);
      if (bilanRes) setBilanData(bilanRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const totalXp = org?.members_data?.reduce((sum, m) => sum + (m.xp || 0), 0) || 0;
  const totalCourses = org?.members_data?.reduce((sum, m) => sum + (m.course_count || 0), 0) || 0;

  const TABS = [
    { id: 'members',      label: 'Membres' },
    { id: 'referentials', label: 'Compétences' },
    { id: 'bilans',       label: 'Bilans' },
  ];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3 animate-fade-up">
          <button
            onClick={() => navigate('/profile')}
            className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="text-white" />
          </button>
          <div>
            <h1 className="font-heading text-xl font-bold text-white">Admin Organisation</h1>
            <p className="text-[#e8e8e8] text-sm">{org?.name || 'Tableau de bord'}</p>
          </div>
        </div>

        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-3 gap-3 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            {[
              { icon: Users,    value: org?.member_count || 0, label: 'Membres',  color: '#ff6b6b' },
              { icon: BookOpen, value: totalCourses,           label: 'Cours',    color: '#20c997' },
              { icon: Zap,      value: totalXp,                label: 'XP Total', color: '#ffc107' },
            ].map((stat, i) => (
              <div key={i} className="kurso-card p-4 text-center">
                <stat.icon size={18} className="mx-auto mb-2" style={{ color: stat.color }} />
                <div className="font-heading font-bold text-white text-lg">{stat.value}</div>
                <div className="text-xs text-[#e8e8e8]">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white/4 p-1 rounded-xl" data-testid="org-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`org-tab-${tab.id}`}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-[#ff6b6b] text-white' : 'text-[#a0a0ab] hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Compétences ───────────────────────────────────────── */}
        {activeTab === 'referentials' && (
          <div className="animate-fade-up">
            <div className="mb-4">
              <h2 className="font-heading font-semibold text-white flex items-center gap-2">
                <Target size={16} className="text-[#ff6b6b]" /> Suivi des compétences
              </h2>
              <p className="text-xs text-[#a0a0ab] mt-0.5">
                Progression des membres par référentiel officiel.
              </p>
            </div>
            <ReferentialPanel spaceId={orgSpace?.id} />
          </div>
        )}

        {/* ── Bilans ───────────────────────────────────────────── */}
        {activeTab === 'bilans' && (
          <div className="animate-fade-up">
            {bilanData ? (
              <>
                <h2 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                  <ClipboardList size={16} className="text-[#ffc107]" /> Vue d'ensemble — Bilans
                </h2>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="kurso-card p-4 text-center border-[#ffc107]/20">
                    <div className="font-heading text-2xl font-bold text-[#ffc107]">
                      {bilanData.aggregated?.completion_rate}%
                    </div>
                    <div className="text-xs text-[#e8e8e8]">
                      {bilanData.aggregated?.completed_bilans}/{bilanData.aggregated?.total_members} bilans
                    </div>
                  </div>
                  <div className="kurso-card p-4">
                    <p className="text-xs font-semibold text-[#e8e8e8] mb-2">Obstacles cités</p>
                    {bilanData.aggregated?.top_obstacles?.slice(0, 2).map((o, i) => (
                      <div key={i} className="text-xs text-[#e8e8e8] truncate">· {o.obstacle} ({o.count})</div>
                    ))}
                  </div>
                </div>
                {bilanData.aggregated?.top_skills?.length > 0 && (
                  <div className="kurso-card p-4">
                    <p className="text-xs font-semibold text-[#e8e8e8] mb-2">Compétences les plus déclarées</p>
                    <div className="flex flex-wrap gap-1.5">
                      {bilanData.aggregated.top_skills.map((s, i) => (
                        <span key={i} className="text-xs bg-[#20c997]/10 text-[#20c997] border border-[#20c997]/20 px-2 py-0.5 rounded-full">
                          {s.skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="kurso-card p-6 text-center">
                <p className="text-[#a0a0ab] text-sm">Données bilans non disponibles.</p>
              </div>
            )}
          </div>
        )}

        {/* ── Membres ───────────────────────────────────────────── */}
        {activeTab === 'members' && (
          <div className="animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-white flex items-center gap-2">
                <TrendingUp size={16} className="text-[#20c997]" /> Progression des membres
              </h2>
            </div>
            {loading ? (
              [1, 2, 3].map(i => <div key={i} className="animate-shimmer h-14 rounded-2xl mb-3" />)
            ) : org?.members_data?.length === 0 ? (
              <div className="kurso-card p-6 text-center">
                <Building2 size={28} className="text-[#e8e8e8] mx-auto mb-2" />
                <p className="text-[#e8e8e8] text-sm">Aucun membre dans l'organisation.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {org.members_data.map((member) => {
                  const bilanMember = bilanData?.member_data?.find(b => b.member_id === member.id) || {};
                  return (
                    <div key={member.id} className="kurso-card p-3 flex items-center gap-3" data-testid={`org-member-${member.id}`}>
                      <div className="w-8 h-8 rounded-xl bg-[#ffc107]/10 flex items-center justify-center shrink-0">
                        <span className="font-heading font-bold text-[#ffc107] text-sm">
                          {member.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{member.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-[#ffc107] font-semibold">{member.xp} XP</span>
                          <span className="text-xs text-[#e8e8e8]">{member.course_count} cours</span>
                          {bilanMember.bilan_completed
                            ? <span className="text-xs text-[#20c997] flex items-center gap-0.5"><CheckCircle size={9} /> Bilan fait</span>
                            : <span className="text-xs text-[#e8e8e8]">Bilan non fait</span>
                          }
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span title="Je sais" className={bilanMember.share_je_sais ? 'text-[#20c997]' : 'text-[#e8e8e8]'}>
                          {bilanMember.share_je_sais ? <Eye size={14} /> : <Lock size={14} />}
                        </span>
                        <span title="Plan d'action" className={bilanMember.share_action_plan ? 'text-[#20c997]' : 'text-[#e8e8e8]'}>
                          {bilanMember.share_action_plan ? <Eye size={14} /> : <Lock size={14} />}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
